import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  createQasRequiredFields,
  editQasRequiredFields,
  deleteQasRequiredFields,
  getQasRequiredFields,
  getAllProductFields,
  getAllTargetMembers,
  saveClaimsVisibility,
  saveClaimsVisibilityProductLevel,
  deleteClaimsVisibilityProductLevel,
} from 'services/qas-require-field/';

const appendPrefixForRecyleContentPercentage = (displayName, fieldFullPath) => {
  if (!fieldFullPath) return fieldFullPath;

  const name = (displayName || '').toLowerCase();

  const map = {
    total: 'total',
    'pre-consumer': 'pre',
    'post-consumer': 'post',
  };

  const key = Object.keys(map).find((k) => name.startsWith(k));

  return key ? `${fieldFullPath}.${map[key]}` : fieldFullPath;
};

/**
 * append .unit to fildFullPath of Retailer Unit
 * @param {*} groups
 * @returns
 */
const addUnitToRetailerUnit = (groups) => {
  if (!Array.isArray(groups) || groups.length === 0) {
    return [];
  }

  const traverse = (group) => {
    // check EPR - Packaging group
    if (group.groupName === 'Packaging - EPR' && group.childGroups) {
      group.childGroups.forEach((child) => {
        if (child.groupName === 'Retailer Unit' && child.fields) {
          child.fields = child.fields.map((field) => ({
            ...field,
            fieldFullPath:
              appendPrefixForRecyleContentPercentage(
                field.displayName,
                field.fieldFullPath,
              ) + '.unit',
          }));
        }
        if (child.groupName === 'Master Case' && child.fields) {
          child.fields = child.fields.map((field) => ({
            ...field,
            fieldFullPath: appendPrefixForRecyleContentPercentage(
              field.displayName,
              field.fieldFullPath,
            ),
          }));
        }
      });
    }

    // recursive childGroups
    if (group.childGroups) {
      group.childGroups.forEach(traverse);
    }
  };

  groups.forEach(traverse);
  return groups;
};

export const QUERY_KEY_QAS_REQUIRE_FIELD = {
  GET_ALL_TARGET_MEMBER: 'qas/get-all-target-members',
  GET_ALL_PRODUCT_FIELDS: 'qas/get-all-product-fields',
  GET_QAS_REQUIRED_FIELDS: 'qas/get-qas-required-fields',
  SAVE_QAS_REQUIRED_FIELDS_PRODUCT_LEVEL:
    'qas/save-qas-required-fields-product-level',
  DELETE_QAS_REQUIRED_FIELDS_PRODUCT_LEVEL:
    'qas/delete-qas-required-fields-product-level',
};

export const useCreateQasRequiredFields = () =>
  useMutation({ mutationFn: createQasRequiredFields });

export const useEditQasRequiredFields = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editQasRequiredFields,
    onSuccess: (response) => {
      if (response?.isSuccess)
        queryClient.invalidateQueries({
          queryKey: [
            QUERY_KEY_QAS_REQUIRE_FIELD.GET_QAS_REQUIRED_FIELDS,
            response?.data?.id,
          ],
        });
      queryClient.invalidateQueries({
        queryKey: ['get-qa-spec-required-fields'], // get a latest required fields
      });
    },
  });
};

export const useDeleteQasRequiredFields = () =>
  useMutation({ mutationFn: deleteQasRequiredFields });

export const useGetQasRequiredFields = (id) =>
  useQuery({
    queryKey: [QUERY_KEY_QAS_REQUIRE_FIELD.GET_QAS_REQUIRED_FIELDS, id],
    queryFn: async () => {
      const reponse = await getQasRequiredFields({ id });
      return reponse?.data;
    },
    enabled: !!id,
  });

export const useGetAllTargetMembers = (searchText) =>
  useQuery({
    queryKey: [QUERY_KEY_QAS_REQUIRE_FIELD.GET_ALL_TARGET_MEMBER, searchText],
    queryFn: async () => {
      const reponse = await getAllTargetMembers({ searchText });
      return reponse?.data;
    },
    enabled: !!searchText,
  });

export const useGetAllProductFields = (option) =>
  useQuery({
    queryKey: [QUERY_KEY_QAS_REQUIRE_FIELD.GET_ALL_PRODUCT_FIELDS],
    queryFn: async () => {
      const response = await getAllProductFields();
      return addUnitToRetailerUnit(response?.data?.groups || []);
    },
    ...option,
    staleTime: Infinity,
  });

export const useSaveClaimsVisibility = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveClaimsVisibility,
    onSuccess: (response) => {
      if (response?.isSuccess) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_QAS_REQUIRE_FIELD.GET_QAS_REQUIRED_FIELDS],
        });
      }
    },
    ...options,
  });
};

export const useSaveClaimsVisibilityProductLevel = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveClaimsVisibilityProductLevel,
    onSuccess: (response) => {
      if (response?.isSuccess) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_QAS_REQUIRE_FIELD.GET_QAS_REQUIRED_FIELDS],
        });
      }
    },
    ...options,
  });
};

export const useDeleteClaimsVisibilityProductLevel = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteClaimsVisibilityProductLevel,
    onSuccess: (response) => {
      if (response?.isSuccess) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_QAS_REQUIRE_FIELD.GET_QAS_REQUIRED_FIELDS],
        });
      }
    },
    ...options,
  });
};
